/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  OrderHistoryListResponse, OrderItemProps,
  OrderProps,
  ReturnProps,
} from '@/account/types';
import { OrderHistoryModel } from '@/account/data/models';
import { transformSingleAddress } from '@/account/data/transforms/transform-customer-address';
import { formatDateToLocale } from '@/account/lib/formatDateToLocale';
import { config } from '@/account/api';
import { merge } from '@adobe-commerce/elsie/lib';

const initialMoneyProps = {
  value: 0,
  currency: 'USD',
};

const transformOrderHistoryTotal = (item: any) => {
  return {
    subtotal: item?.total?.subtotal ?? initialMoneyProps,
    grandTotal: item?.total?.grand_total ?? initialMoneyProps,
    grandTotalExclTax: item?.total?.grand_total_excl_tax ?? initialMoneyProps,
    totalGiftcard: item?.total?.total_giftcard ?? initialMoneyProps,
    subtotalExclTax: item?.total?.subtotal_excl_tax ?? initialMoneyProps,
    subtotalInclTax: item?.total?.subtotal_incl_tax ?? initialMoneyProps,
    taxes: item?.total?.taxes ?? [],
    totalTax: item?.total?.total_tax ?? initialMoneyProps,
    totalShipping: item?.total?.total_shipping ?? initialMoneyProps,
    discounts: item?.total?.discounts ?? [],
  };
};

export const transformOrderHistoryList = (
  response: OrderHistoryListResponse
): OrderHistoryModel | null => {
  if (!response.data?.customer?.orders) return null;

  const returns = response?.data?.customer?.returns ?? [];
  const items = response?.data?.customer?.orders?.items ?? [];

  const model = {
    items: items.map((item: OrderProps) => {
      return {
        items: item?.items.map((element: OrderItemProps) => ({
          status: element?.status ?? '',
          productName: element?.product_name ?? '',
          id: element?.id,
          quantityOrdered: element?.quantity_ordered ?? 0,
          quantityShipped: element?.quantity_shipped ?? 0,
          quantityInvoiced: element?.quantity_invoiced ?? 0,
          sku: element?.product_sku ?? '',
          urlKey: element?.product_url_key ?? '',
          topLevelSku: element?.product?.sku ?? '',
          product: {
            smallImage: {
              url: element?.product?.small_image?.url ?? '',
            },
          },
        })),
        token: item?.token,
        email: item?.email,
        shippingMethod: item?.shipping_method,
        paymentMethods: item?.payment_methods ?? [],
        shipments: item?.shipments ?? [],
        id: item?.id,
        carrier: item?.carrier,
        status: item?.status,
        number: item?.number,
        returns: returns?.items?.filter(
          (returnItem: ReturnProps) => returnItem.order.id === item.id
        ),
        orderDate: formatDateToLocale(item.order_date),
        shippingAddress: transformSingleAddress(item.shipping_address),
        billingAddress: transformSingleAddress(item.billing_address),
        total: transformOrderHistoryTotal(item),
      };
    }),
    pageInfo: {
      pageSize: response?.data?.customer?.orders?.page_info?.page_size ?? 10,
      totalPages: response?.data?.customer?.orders?.page_info?.total_pages ?? 1,
      currentPage:
        response?.data?.customer?.orders?.page_info?.current_page ?? 1,
    },
    totalCount: response?.data?.customer?.orders?.total_count ?? 0,
    dateOfFirstOrder:
      response?.data?.customer?.orders?.date_of_first_order ?? '',
  };

  return merge(
    model, // default transformer
    config?.getConfig()?.models?.OrderHistoryModel?.transformer?.(response.data) // custom transformer
  );
};
